// src/components/MassageClinicSearch.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MapPin, Search, Phone, Clock, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

export default function MassageClinicSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const data = await api.massageClinics.search(searchQuery);
      setClinics(data.clinics || []);
    } catch (error) {
      console.error("Search error:", error);
      // Show sample data for demo
      setClinics([
        {
          id: 1,
          name: "Serenity Massage Spa",
          address: "123 Main Street, Toronto, ON M5V 2T6",
          phone: "(416) 555-0123",
          hours: "Mon-Sat: 9AM-8PM, Sun: 10AM-6PM",
          distance: "0.5 km",
          services: ["Swedish Massage", "Deep Tissue", "Hot Stone"],
        },
        {
          id: 2,
          name: "Tranquil Touch Wellness",
          address: "456 Queen Street W, Toronto, ON M5V 2A8",
          phone: "(416) 555-0456",
          hours: "Mon-Sun: 8AM-9PM",
          distance: "1.2 km",
          services: ["Relaxation Massage", "Sports Massage", "Aromatherapy"],
        },
        {
          id: 3,
          name: "Healing Hands Massage",
          address: "789 King Street E, Toronto, ON M5A 1M5",
          phone: "(416) 555-0789",
          hours: "Tue-Sun: 10AM-7PM",
          distance: "2.1 km",
          services: ["Deep Tissue", "Prenatal Massage", "Thai Massage"],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            ← Back to Home
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Find Participating Clinics</h1>
              <p className="text-gray-600 mt-1">
                Discover massage clinics where you can use your gift card
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Enter city, postal code, or address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  {loading ? "Searching..." : "Search"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Results */}
        {clinics.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {clinics.length} Clinics Found
            </h2>

            {clinics.map((clinic) => (
              <Card key={clinic.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {clinic.name}
                      </h3>
                      {clinic.distance && (
                        <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full mb-3">
                          {clinic.distance}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{clinic.address}</span>
                    </div>

                    {clinic.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={`tel:${clinic.phone}`}
                          className="text-emerald-600 hover:underline"
                        >
                          {clinic.phone}
                        </a>
                      </div>
                    )}

                    {clinic.hours && (
                      <div className="flex items-start gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{clinic.hours}</span>
                      </div>
                    )}

                    {clinic.services && clinic.services.length > 0 && (
                      <div className="pt-2">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Services Available:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {clinic.services.map((service, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        window.open(
                          `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}`,
                          "_blank"
                        )
                      }
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Get Directions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && clinics.length === 0 && searchQuery && (
          <Card>
            <CardContent className="py-12 text-center">
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Clinics Found
              </h3>
              <p className="text-gray-600">
                Try searching with a different location or postal code
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Banner */}
        {!searchQuery && (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold text-emerald-900 mb-2">
                How to Use Your Gift Card
              </h3>
              <ul className="space-y-2 text-sm text-emerald-800">
                <li>• Search for participating massage clinics near you</li>
                <li>• Call the clinic to book your appointment</li>
                <li>• Mention you have a gift card when booking</li>
                <li>• Present your gift card code at the time of service</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
